import { zodResolver } from '@hookform/resolvers/zod';

import { Skeleton, View } from '@zinc/ui';
import BzHooksFormAutosave, { AutosaveStatusProvider } from 'bz-utilities/autosave/hooks-form';
import { TimeUnit } from 'bz-utilities/global/date';
import withDisplayName from 'bz-utilities/hoc/display-name';
import { useDebounce } from 'bz-utilities/hooks/debounce';
import useUnmountEffect from 'bz-utilities/hooks/unmount-effect';

import _ from 'lodash';

import type { PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { DefaultValues, FieldValues, SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';

import zodValidationSchema from 'tm3-core/validation';

import HooksFormDebugger from './debugger';
import { FormUtilsProvider } from './utils';

export interface IProps<T extends FieldValues> extends PropsWithChildren {
  autoSave?: boolean;
  autoSaveTimer?: number;
  data?: T;
  debug?: boolean;
  defaultValues?: DefaultValues<T>;
  wrapInForm?: boolean;
  validatorKey: T;

  onSubmit?: SubmitHandler<T>;
  loadingComponent?: React.ReactNode;
  showLoader?: boolean;
}

type GenericFC = <T extends FieldValues>(
  ...args: Parameters<React.FC<IProps<T>>>
) => ReturnType<React.FC>;

const HooksForm = React.memo(
  withDisplayName(
    <T extends FieldValues>({
      onSubmit,
      children,
      debug,
      wrapInForm = true,
      validatorKey,
      defaultValues = { ...validatorKey } as DefaultValues<T>,
      data,
      autoSave = false,
      showLoader = false,
      loadingComponent
    }: IProps<T>) => {
      const k = validatorKey.constructor.name;
      const validator = k && zodValidationSchema[k as keyof typeof zodValidationSchema];
      const [values, setValues] = useState(() => ({ ...data }));

      // Use zodResolver which properly handles Zod validation including refine callbacks
      // The cache.config is accessible in the validation schema's refine functions
      const resolver = useMemo(() => {
        if (!validator) return undefined;
        // Force async mode for zodResolver to ensure superRefine callbacks execute
        return zodResolver(validator, {}, { raw: true });
      }, [validator]);

      const form = useForm<T>({
        resolver,
        mode: 'onChange', // Validate on every change
        reValidateMode: 'onChange',
        defaultValues: !!data ? undefined : defaultValues,
        values: values as T
      });

      const onSubmited = useDebounce(
        () => {
          form.reset(form.getValues(), { keepIsSubmitted: false });
        },
        [form.getValues, form.reset],
        3 * TimeUnit.second
      );

      const submitHandler: SubmitHandler<T> = useCallback(
        async (dto) => {
          await onSubmit?.(dto);
          form.reset(dto);
          onSubmited();
        },
        [onSubmit, form.reset]
      );

      useEffect(() => {
        if (!_.isEqual(values, Object.assign({}, data))) {
          setValues({ ...data });
        }
      }, [data, values]);

      useEffect(() => {
        form.trigger(); // pre validate the form
      }, [form]);

      useUnmountEffect(onSubmited.cancel);
      if (data === undefined && loadingComponent !== null && showLoader) {
        return (
          <>
            {loadingComponent ?? (
              <View gap={2}>
                <Skeleton width="100%" height={14} />
                <Skeleton width="60%" height={8} />
                <Skeleton width="60%" height={8} />
                <Skeleton width="60%" height={8} />
                <Skeleton width="60%" height={8} />
                <Skeleton width="60%" height={8} />
                <Skeleton width="60%" height={8} />
              </View>
            )}
          </>
        );
      }
      const innerContent = (
        <FormUtilsProvider autoSave={autoSave}>
          {children}
          {autoSave && <BzHooksFormAutosave saveFn={submitHandler as SubmitHandler<FieldValues>} />}
          {debug && <HooksFormDebugger />}
        </FormUtilsProvider>
      );
      const content = (
        <FormProvider {...form}>
          {autoSave ? (
            <AutosaveStatusProvider>{innerContent}</AutosaveStatusProvider>
          ) : (
            innerContent
          )}
        </FormProvider>
      );

      if (!wrapInForm) {
        return content;
      }

      return <form onSubmit={form.handleSubmit(submitHandler)}>{content}</form>;
    },
    'HooksForm'
  )
);

export default HooksForm as GenericFC;
